'use client';

import { useState, useEffect, Suspense } from 'react';
import { useSession } from 'next-auth/react';
import { useSearchParams, useRouter } from 'next/navigation';
import { Sparkles, Volume2, Check, Crown, Zap, Star, BookOpen, Play, Target, Globe, Mic, Heart, UtensilsCrossed, ChefHat, Clock, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import AudioBookVideoPlayer from './audio-book/audio-book-video-player';
import { PurchaseSuccessModal } from './purchase-success-modal';

interface FeaturedAudioBook {
  id: string;
  title: string;
  coverImageUrl: string | null;
  pageCount: number;
  totalDuration: number | null;
  watermark: boolean;
  createdAt: string;
}

interface Recipe {
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  coverImage: string | null;
  tags: string[];
  createdAt: string;
  readingTime: number;
}

const PRICING_PACKAGES = [
  {
    type: 'credits10',
    name: 'Starter',
    credits: 10,
    price: '$15',
    pricePerBook: '$1.50',
    features: ['10 credits ($1.50/credit)', '10 coloring books or', '3 audio picture books', 'No watermarks', 'Forever access'],
  },
  {
    type: 'credits25',
    name: 'Family',
    credits: 25,
    price: '$30',
    pricePerBook: '$1.20',
    features: ['25 credits ($1.20/credit)', '25 coloring books or', '8 audio picture books', 'No watermarks', 'Forever access'],
    featured: true,
  },
  {
    type: 'credits60',
    name: 'Pro',
    credits: 60,
    price: '$60',
    pricePerBook: '$1.00',
    features: ['60 credits ($1.00/credit)', '60 coloring books or', '20 audio picture books', 'No watermarks', 'Forever access'],
  },
];

const SUBSCRIPTIONS = [
  {
    id: 'amaya_plus',
    name: 'Amaya Plus',
    price: '$19.99/mo',
    credits: 30,
    features: [
      '30 credits per month',
      '~10 audio books or 30 coloring books',
      '100+ audio story gallery access',
      'No watermarks on all creations',
      'Priority customer support',
      'Cancel anytime'
    ],
    description: 'Perfect for families',
  },
  {
    id: 'amaya_pro',
    name: 'Amaya Pro',
    price: '$49.99/mo',
    credits: 100,
    features: [
      '100 credits per month',
      '~33 audio books or 100 coloring books',
      'Everything in Amaya Plus',
      'Custom voice selection',
      'Bilingual story mode',
      'Early access to new features',
      'Priority generation queue'
    ],
    featured: true,
    description: 'Best for power users',
  },
];

function HomePage2026Content() {
  const { data: session } = useSession();
  const searchParams = useSearchParams();
  const router = useRouter();
  const [loading, setLoading] = useState<string | null>(null);
  const [showPurchaseSuccess, setShowPurchaseSuccess] = useState(false);
  const [purchaseDetails, setPurchaseDetails] = useState<{type: 'credits' | 'subscription'; amount: number} | null>(null);
  const [featuredBooks, setFeaturedBooks] = useState<FeaturedAudioBook[]>([]);
  const [loadingFeaturedBooks, setLoadingFeaturedBooks] = useState(true);
  const [recentRecipes, setRecentRecipes] = useState<Recipe[]>([]);
  const [loadingRecipes, setLoadingRecipes] = useState(true);

  // Check for purchase success from URL params
  useEffect(() => {
    const purchase = searchParams.get('purchase');
    const type = searchParams.get('type');
    const amount = searchParams.get('amount');

    if (purchase === 'success' && type && amount) {
      setPurchaseDetails({
        type: type as 'credits' | 'subscription',
        amount: parseInt(amount, 10)
      });
      setShowPurchaseSuccess(true);
      router.replace('/', { scroll: false });
    }
  }, [searchParams, router]);

  // Fetch featured audio books
  useEffect(() => {
    const fetchFeaturedBooks = async () => {
      try {
        const res = await fetch('/api/audio-book/featured');
        if (!res.ok) {
          throw new Error('Failed to fetch featured audio books');
        }
        const data = await res.json();
        setFeaturedBooks(data.audioBooks || []);
      } catch (err: any) {
        console.error('Error fetching featured audio books:', err);
        // Silently fail - the section will show empty state
      } finally {
        setLoadingFeaturedBooks(false);
      }
    };
    fetchFeaturedBooks();
  }, []);

  // Fetch recent recipes
  useEffect(() => {
    const fetchRecentRecipes = async () => {
      try {
        const res = await fetch('/api/blogs?category=EATS&limit=3');
        if (!res.ok) {
          throw new Error('Failed to fetch recipes');
        }
        const data = await res.json();
        setRecentRecipes(data.posts || []);
      } catch (err: any) {
        console.error('Error fetching recipes:', err);
        // Silently fail - the section will show empty state
      } finally {
        setLoadingRecipes(false);
      }
    };
    fetchRecentRecipes();
  }, []);

  async function proceedToPurchase(packageType: string) {
    setLoading(packageType);

    try {
      const res = await fetch('/api/checkout/credits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ packageType }),
      });

      if (!res.ok) throw new Error('Checkout failed');

      const data = await res.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Failed to start checkout. Please try again.');
      setLoading(null);
    }
  }

  async function proceedToSubscription(plan: 'amaya_plus' | 'amaya_pro') {
    setLoading(plan);

    try {
      const res = await fetch('/api/checkout/subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan }),
      });

      if (!res.ok) throw new Error('Subscription failed');

      const data = await res.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Subscribe error:', error);
      alert('Failed to start subscription. Please try again.');
      setLoading(null);
    }
  }

  async function handlePurchase(packageType: string) {
    if (!session) {
      sessionStorage.setItem('pendingPurchase', JSON.stringify({ 
        type: 'credits',
        packageType 
      }));
      // Redirect to login page with callback
      const callbackUrl = encodeURIComponent(window.location.href);
      router.push(`/login?callbackUrl=${callbackUrl}`);
      return;
    }

    proceedToPurchase(packageType);
  }

  async function handleSubscribe(plan: 'amaya_plus' | 'amaya_pro') {
    if (!session) {
      sessionStorage.setItem('pendingPurchase', JSON.stringify({ 
        type: 'subscription',
        plan
      }));
      // Redirect to login page with callback
      const callbackUrl = encodeURIComponent(window.location.href);
      router.push(`/login?callbackUrl=${callbackUrl}`);
      return;
    }

    proceedToSubscription(plan);
  }

  return (
    <>
      {/* Component Styles - Must be at top level */}
      <style jsx>{`
        .benefits-container {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 1.5rem;
          align-items: center;
          margin-bottom: 3rem;
          padding: 0 1.5rem;
        }

        .benefit-badge {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: clamp(0.8125rem, 1.2vw, 0.9375rem);
          color: #666;
          font-weight: 600;
          padding: 0.375rem 0.875rem;
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(52, 211, 153, 0.08) 100%);
          border-radius: 100px;
          border: 1.5px solid rgba(16, 185, 129, 0.2);
          white-space: nowrap;
        }

        .benefit-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 16px;
          height: 16px;
          background: linear-gradient(135deg, #10B981 0%, #34D399 100%);
          border-radius: 50%;
          flex-shrink: 0;
        }

        @media (max-width: 768px) {
          .benefits-container {
            gap: 0.5rem;
            padding: 0 0.75rem;
          }

          .benefit-badge {
            font-size: 0.6875rem;
            padding: 0.25rem 0.5rem;
            gap: 0.35rem;
          }

          .benefit-icon {
            width: 12px;
            height: 12px;
          }
        }

        @media (max-width: 480px) {
          .benefits-container {
            gap: 0.375rem;
            padding: 0 0.5rem;
          }

          .benefit-badge {
            font-size: 0.625rem;
            padding: 0.25rem 0.4rem;
            gap: 0.25rem;
          }

          .benefit-icon {
            width: 10px;
            height: 10px;
          }
        }

        /* Trust Badge - Mobile Responsive */
        @media (max-width: 768px) {
          .trust-badge {
            gap: 0.5rem !important;
            padding: 0.625rem 1.25rem !important;
            margin-bottom: 1.5rem !important;
          }

          .trust-stars span {
            font-size: 0.875rem !important;
          }

          .trust-text {
            font-size: 0.75rem !important;
            letter-spacing: 0.01em !important;
          }

          /* Feature Cards Grid - Mobile */
          .home-2026-feature-grid {
            grid-template-columns: 1fr !important;
            gap: 1.5rem !important;
            padding: 0 1rem !important;
          }

          .feature-card-2026 {
            padding: 1.5rem !important;
          }

          /* Pricing Cards Grid - Mobile */
          #credit-packages-grid {
            grid-template-columns: 1fr !important;
            gap: 1.5rem !important;
            padding: 0 1rem !important;
          }

          .pricing-card-2026 {
            padding: 1.5rem !important;
            max-width: 400px !important;
            margin: 0 auto !important;
          }

          /* Subscription Cards Grid - Mobile */
          #subscriptions-grid {
            grid-template-columns: 1fr !important;
            gap: 1.5rem !important;
            padding: 0 1rem !important;
          }

          .subscription-card-2026 {
            padding: 1.5rem !important;
            max-width: 400px !important;
            margin: 0 auto !important;
          }
        }

        @media (max-width: 480px) {
          .trust-badge {
            gap: 0.375rem !important;
            padding: 0.5rem 1rem !important;
            border-width: 1.5px !important;
          }

          .trust-stars {
            gap: 0.125rem !important;
          }

          .trust-stars span {
            font-size: 0.75rem !important;
          }

          .trust-text {
            font-size: 0.625rem !important;
          }

          /* Feature Cards Grid - Small Mobile */
          .home-2026-feature-grid {
            padding: 0 0.75rem !important;
            gap: 1.25rem !important;
          }

          .feature-card-2026 {
            padding: 1.25rem !important;
          }

          /* Pricing Cards Grid - Small Mobile */
          #credit-packages-grid {
            padding: 0 0.75rem !important;
            gap: 1.25rem !important;
          }

          .pricing-card-2026 {
            padding: 1.25rem !important;
            max-width: 100% !important;
          }

          /* Subscription Cards Grid - Small Mobile */
          #subscriptions-grid {
            padding: 0 0.75rem !important;
            gap: 1.25rem !important;
          }

          .subscription-card-2026 {
            padding: 1.25rem !important;
            max-width: 100% !important;
          }
        }

        /* Recipe Section Animations */
        @keyframes float {
          0%, 100% { 
            transform: translateY(0) scale(1); 
          }
          50% { 
            transform: translateY(-15px) scale(1.02); 
          }
        }
        
        @keyframes buttonShine {
          0% {
            left: -100%;
          }
          100% {
            left: 100%;
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .recipe-cta {
          transition: gap 0.3s ease;
        }
        
        div:hover .recipe-cta {
          gap: 0.875rem;
        }
        
        div:hover .shine-effect {
          left: 100%;
        }
      `}</style>

      {purchaseDetails && (
        <PurchaseSuccessModal
          isOpen={showPurchaseSuccess}
          onClose={() => {
            setShowPurchaseSuccess(false);
            setPurchaseDetails(null);
          }}
          purchaseType={purchaseDetails.type}
          amount={purchaseDetails.amount}
        />
      )}

      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(to bottom, #F9F5F0 0%, #FAF7F2 100%)',
      }}>
        {/* Hero Section with Video */}
        <div style={{ 
          maxWidth: '100%',
          margin: '0 auto',
          padding: 'clamp(2rem, 4vw, 3rem) 0',
          textAlign: 'center',
        }}>
          {/* Magical Gradient Title Above Video */}
          <h1 style={{
            fontSize: 'clamp(2.5rem, 7vw, 5.5rem)',
            fontWeight: 900,
            lineHeight: 1.1,
            marginBottom: '1rem',
            padding: '0 1.5rem',
            background: 'linear-gradient(90deg, #F8C8A0 0%, #E8B4D1 20%, #C5B4FF 40%, #A8D8E6 60%, #B5F2E0 80%, #C4E9FF 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            letterSpacing: '-0.03em',
            textShadow: '0 0 40px rgba(197, 180, 255, 0.3)',
            animation: 'gradientShift 8s ease infinite',
          }}>
            Bring Your Child's Imagination to Life
          </h1>

          {/* Elegant Subtitle */}
          <p style={{
            fontSize: 'clamp(1rem, 2vw, 1.25rem)',
            color: '#5A5A5A',
            maxWidth: '800px',
            margin: '0 auto 3rem',
            lineHeight: 1.6,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            padding: '0 1.5rem',
          }}>
            Create Montessori-inspired AI coloring books, bilingual audio stories, and language learning adventures in minutes.
          </p>

          {/* Video Player with Enhanced Shadow - Full Width */}
          <div style={{
            position: 'relative',
            maxWidth: '900px',
            margin: '0 auto',
            padding: '0 1.5rem',
          }}>
            <div style={{
              position: 'relative',
              borderRadius: '24px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.5)',
              transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-8px)';
              e.currentTarget.style.boxShadow = '0 30px 80px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.5)';
            }}>
              <AudioBookVideoPlayer 
                videoSrc="https://pub-c66b80290e37412f86676f07b6cbcd28.r2.dev/ai-generated-audio-image-kids-stories.mp4"
                posterSrc="https://pub-c66b80290e37412f86676f07b6cbcd28.r2.dev/Amaya%20Story.webp"
              />
            </div>
          </div>

          {/* CTA Buttons - BELOW VIDEO */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '1.5rem',
            marginTop: '3rem',
            marginBottom: '2rem',
            padding: '0 1.5rem',
          }}>
            <Link href="/coloring" style={{ textDecoration: 'none' }}>
              <button style={{
                padding: 'clamp(1rem, 2vw, 1.25rem) clamp(2rem, 4vw, 3rem)',
                background: 'linear-gradient(135deg, #A8D8E6 0%, #C9B6E4 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '100px',
                fontSize: 'clamp(1.125rem, 2vw, 1.375rem)',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 8px 30px rgba(168, 216, 230, 0.4)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                minWidth: '280px',
                justifyContent: 'center',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(168, 216, 230, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(168, 216, 230, 0.4)';
              }}>
                <Sparkles size={24} />
                Create Free Coloring Book
              </button>
            </Link>

            <Link href="/audio-book/create" style={{ textDecoration: 'none' }}>
              <button style={{
                padding: 'clamp(1rem, 2vw, 1.25rem) clamp(2rem, 4vw, 3rem)',
                background: 'linear-gradient(135deg, #C9B6E4 0%, #A8D8E6 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '100px',
                fontSize: 'clamp(1.125rem, 2vw, 1.375rem)',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 8px 30px rgba(201, 182, 228, 0.4)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                minWidth: '280px',
                justifyContent: 'center',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(201, 182, 228, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(201, 182, 228, 0.4)';
              }}>
                <Volume2 size={24} />
                Create Audio Picture Book
              </button>
            </Link>
          </div>

          {/* Benefit Badges - BELOW BUTTONS */}
          <div className="benefits-container">
            {[
              'Free Preview Available',
              'No Credit Card Required',
              'Ready in 2-3 Minutes'
            ].map((benefit, i) => (
              <div key={i} className="benefit-badge">
                <div className="benefit-icon">
                  <Check 
                    size={8} 
                    strokeWidth={3.5} 
                    style={{ color: 'white' }} 
                  />
                </div>
                {benefit}
              </div>
            ))}
          </div>
        </div>

        {/* Why Parents & Teachers Love Section - 2026 Award-Winning Design */}
        <div style={{ 
          position: 'relative',
          maxWidth: '1400px', 
          margin: '0 auto',
          padding: 'clamp(2.5rem, 5vw, 4rem) 1.5rem',
          overflow: 'hidden',
        }}>
          {/* Ambient Background Elements */}
          <div style={{
            position: 'absolute',
            top: '10%',
            left: '-5%',
            width: '400px',
            height: '400px',
            background: 'radial-gradient(circle, rgba(168, 216, 230, 0.08) 0%, transparent 70%)',
            borderRadius: '50%',
            pointerEvents: 'none',
            filter: 'blur(40px)',
          }} />
          <div style={{
            position: 'absolute',
            bottom: '20%',
            right: '-5%',
            width: '500px',
            height: '500px',
            background: 'radial-gradient(circle, rgba(201, 182, 228, 0.08) 0%, transparent 70%)',
            borderRadius: '50%',
            pointerEvents: 'none',
            filter: 'blur(40px)',
          }} />

          {/* Section Header */}
          <div style={{ textAlign: 'center', marginBottom: 'clamp(2.5rem, 5vw, 4rem)', position: 'relative', zIndex: 1 }}>
            {/* Animated Trust Badge */}
            <div className="trust-badge" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.875rem 2rem',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(52, 211, 153, 0.08) 100%)',
              backdropFilter: 'blur(16px)',
              border: '2px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '100px',
              marginBottom: '2rem',
              boxShadow: '0 8px 32px rgba(16, 185, 129, 0.15), inset 0 0 20px rgba(255, 255, 255, 0.1)',
              animation: 'gentle-pulse 4s ease-in-out infinite',
            }}>
              <div className="trust-stars" style={{
                display: 'flex',
                gap: '0.25rem',
                animation: 'star-twinkle 2s ease-in-out infinite',
              }}>
                <span style={{ fontSize: '1.125rem' }}>⭐</span>
                <span style={{ fontSize: '1.125rem' }}>⭐</span>
                <span style={{ fontSize: '1.125rem' }}>⭐</span>
                <span style={{ fontSize: '1.125rem' }}>⭐</span>
                <span style={{ fontSize: '1.125rem' }}>⭐</span>
              </div>
              <span className="trust-text" style={{
                fontSize: '1rem',
                fontWeight: 800,
                background: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
              }}>
                Trusted by 50,000+ Happy Families
              </span>
        </div>

            <h2 style={{
              fontSize: 'clamp(2.5rem, 7vw, 5rem)',
            fontWeight: 900,
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2D2D2D 50%, #1a1a1a 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
              marginBottom: '1.5rem',
            letterSpacing: '-0.03em',
              lineHeight: 1.05,
              textShadow: '0 0 40px rgba(0, 0, 0, 0.05)',
            }}>
              Where Learning Becomes Adventure
            </h2>
          <p style={{
              fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
              background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            fontWeight: 600,
              maxWidth: '900px',
              margin: '0 auto 1.5rem',
              lineHeight: 1.5,
            letterSpacing: '-0.01em',
          }}>
              AI-powered stories that grow with your child, making every learning moment magical
          </p>
          <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, rgba(168, 216, 230, 0.1) 0%, rgba(201, 182, 228, 0.1) 100%)',
              borderRadius: '100px',
              fontSize: '0.9375rem',
              fontWeight: 700,
              color: '#667eea',
              border: '1.5px solid rgba(168, 216, 230, 0.3)',
            }}>
              <Sparkles size={18} strokeWidth={2.5} />
              <span>Powered by Montessori Principles</span>
          </div>
        </div>

          {/* Feature Cards Grid - 4 Cards with Enhanced 2026 Design */}
          <div className="home-2026-feature-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 'clamp(1.25rem, 2.5vw, 2.5rem)',
            maxWidth: '1400px',
            margin: '0 auto',
            position: 'relative',
            zIndex: 1,
          }}>
            {/* Card 1: Intelligent Learning System */}
            <div className="feature-card-2026" style={{
              background: 'linear-gradient(to bottom, #ffffff 0%, #fafbfc 100%)',
              borderRadius: '28px',
              padding: 'clamp(2rem, 3.5vw, 3rem)',
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.02)',
              transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              border: '1px solid rgba(0, 0, 0, 0.04)',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
              e.currentTarget.style.boxShadow = '0 20px 60px rgba(102, 126, 234, 0.18), 0 8px 24px rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.3)';
              const glow = e.currentTarget.querySelector('.card-glow') as HTMLElement;
              if (glow) glow.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.02)';
              e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.04)';
              const glow = e.currentTarget.querySelector('.card-glow') as HTMLElement;
              if (glow) glow.style.opacity = '0';
            }}>
              {/* Hover Glow Effect */}
              <div className="card-glow" style={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(circle at center, rgba(102, 126, 234, 0.08) 0%, transparent 70%)',
                opacity: 0,
                transition: 'opacity 0.5s ease',
                pointerEvents: 'none',
              }} />
              
              <div style={{
                width: 'clamp(90px, 18vw, 110px)',
                height: 'clamp(90px, 18vw, 110px)',
                borderRadius: '28px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.75rem',
                boxShadow: '0 16px 48px rgba(102, 126, 234, 0.4), inset 0 2px 12px rgba(255, 255, 255, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.2)',
                position: 'relative',
                transform: 'rotate(-3deg)',
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(8px)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'rotate(0deg) scale(1.15) translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 24px 64px rgba(102, 126, 234, 0.5), inset 0 2px 12px rgba(255, 255, 255, 0.5), 0 0 0 2px rgba(255, 255, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'rotate(-3deg) scale(1)';
                e.currentTarget.style.boxShadow = '0 16px 48px rgba(102, 126, 234, 0.4), inset 0 2px 12px rgba(255, 255, 255, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.2)';
              }}>
                {/* Animated Glow Ring */}
                <div style={{
                  position: 'absolute',
                  inset: '-4px',
                  borderRadius: '32px',
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.3), rgba(118, 75, 162, 0.3))',
                  filter: 'blur(12px)',
                  opacity: 0.6,
                  animation: 'pulse 3s ease-in-out infinite',
                }} />
                <Target 
                  size={48} 
                  strokeWidth={2.5} 
                  style={{ 
                    color: 'white',
                    position: 'relative',
                    zIndex: 1,
                    filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.2))',
                  }} 
                />
              </div>
              <h3 style={{
                fontSize: 'clamp(1.25rem, 2.2vw, 1.5rem)',
                fontWeight: 800,
                color: '#1a1a1a',
                marginBottom: '1rem',
                lineHeight: 1.2,
                letterSpacing: '-0.01em',
              }}>
                Intelligent Learning System
              </h3>
              <p style={{
                fontSize: 'clamp(0.9375rem, 1.6vw, 1.0625rem)',
                color: '#64748b',
                lineHeight: 1.7,
                margin: 0,
                fontWeight: 500,
              }}>
                Stories that adapt to your child's age, interests, and learning pace—creating unique experiences every time
              </p>
            </div>

            {/* Card 2: Montessori Learning */}
            <div className="feature-card-2026" style={{
              background: 'linear-gradient(to bottom, #ffffff 0%, #fafbfc 100%)',
              borderRadius: '28px',
              padding: 'clamp(2rem, 3.5vw, 3rem)',
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.02)',
              transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              border: '1px solid rgba(0, 0, 0, 0.04)',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
              e.currentTarget.style.boxShadow = '0 20px 60px rgba(232, 198, 180, 0.18), 0 8px 24px rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.borderColor = 'rgba(232, 198, 180, 0.3)';
              const glow = e.currentTarget.querySelector('.card-glow') as HTMLElement;
              if (glow) glow.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.02)';
              e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.04)';
              const glow = e.currentTarget.querySelector('.card-glow') as HTMLElement;
              if (glow) glow.style.opacity = '0';
            }}>
              <div className="card-glow" style={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(circle at center, rgba(232, 198, 180, 0.08) 0%, transparent 70%)',
                opacity: 0,
                transition: 'opacity 0.5s ease',
                pointerEvents: 'none',
              }} />
              
              <div style={{
                width: 'clamp(90px, 18vw, 110px)',
                height: 'clamp(90px, 18vw, 110px)',
                borderRadius: '28px',
                background: 'linear-gradient(135deg, #E8C6B4 0%, #D9A88E 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.75rem',
                boxShadow: '0 16px 48px rgba(232, 198, 180, 0.45), inset 0 2px 12px rgba(255, 255, 255, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.2)',
                position: 'relative',
                transform: 'rotate(3deg)',
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(8px)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'rotate(0deg) scale(1.15) translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 24px 64px rgba(232, 198, 180, 0.6), inset 0 2px 12px rgba(255, 255, 255, 0.5), 0 0 0 2px rgba(255, 255, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'rotate(3deg) scale(1)';
                e.currentTarget.style.boxShadow = '0 16px 48px rgba(232, 198, 180, 0.45), inset 0 2px 12px rgba(255, 255, 255, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.2)';
              }}>
                {/* Animated Glow Ring */}
                <div style={{
                  position: 'absolute',
                  inset: '-4px',
                  borderRadius: '32px',
                  background: 'linear-gradient(135deg, rgba(232, 198, 180, 0.4), rgba(217, 168, 142, 0.4))',
                  filter: 'blur(12px)',
                  opacity: 0.6,
                  animation: 'pulse 3s ease-in-out infinite 0.5s',
                }} />
                <Sparkles 
                  size={48} 
                  strokeWidth={2.5} 
                  style={{ 
                    color: 'white',
                    position: 'relative',
                    zIndex: 1,
                    filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.2))',
                  }} 
                />
              </div>
              <h3 style={{
                fontSize: 'clamp(1.25rem, 2.2vw, 1.5rem)',
                fontWeight: 800,
                color: '#1a1a1a',
                marginBottom: '1rem',
                lineHeight: 1.2,
                letterSpacing: '-0.01em',
              }}>
                Montessori Learning
              </h3>
              <p style={{
                fontSize: 'clamp(0.9375rem, 1.6vw, 1.0625rem)',
                color: '#64748b',
                lineHeight: 1.7,
                margin: 0,
                fontWeight: 500,
              }}>
                Scientifically-backed methods that nurture natural curiosity and self-directed learning through engaging stories
              </p>
            </div>

            {/* Card 3: Multilingual Support */}
            <div className="feature-card-2026" style={{
              background: 'linear-gradient(to bottom, #ffffff 0%, #fafbfc 100%)',
              borderRadius: '28px',
              padding: 'clamp(2rem, 3.5vw, 3rem)',
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.02)',
              transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              border: '1px solid rgba(0, 0, 0, 0.04)',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
              e.currentTarget.style.boxShadow = '0 20px 60px rgba(123, 126, 200, 0.18), 0 8px 24px rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.borderColor = 'rgba(123, 126, 200, 0.3)';
              const glow = e.currentTarget.querySelector('.card-glow') as HTMLElement;
              if (glow) glow.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.02)';
              e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.04)';
              const glow = e.currentTarget.querySelector('.card-glow') as HTMLElement;
              if (glow) glow.style.opacity = '0';
            }}>
              <div className="card-glow" style={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(circle at center, rgba(123, 126, 200, 0.08) 0%, transparent 70%)',
                opacity: 0,
                transition: 'opacity 0.5s ease',
                pointerEvents: 'none',
              }} />
              
              <div style={{
                width: 'clamp(90px, 18vw, 110px)',
                height: 'clamp(90px, 18vw, 110px)',
                borderRadius: '28px',
                background: 'linear-gradient(135deg, #7B7EC8 0%, #5B5DAF 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.75rem',
                boxShadow: '0 16px 48px rgba(123, 126, 200, 0.45), inset 0 2px 12px rgba(255, 255, 255, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.2)',
                position: 'relative',
                transform: 'rotate(-3deg)',
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(8px)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'rotate(0deg) scale(1.15) translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 24px 64px rgba(123, 126, 200, 0.6), inset 0 2px 12px rgba(255, 255, 255, 0.5), 0 0 0 2px rgba(255, 255, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'rotate(-3deg) scale(1)';
                e.currentTarget.style.boxShadow = '0 16px 48px rgba(123, 126, 200, 0.45), inset 0 2px 12px rgba(255, 255, 255, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.2)';
              }}>
                {/* Animated Glow Ring */}
                <div style={{
                  position: 'absolute',
                  inset: '-4px',
                  borderRadius: '32px',
                  background: 'linear-gradient(135deg, rgba(123, 126, 200, 0.4), rgba(91, 93, 175, 0.4))',
                  filter: 'blur(12px)',
                  opacity: 0.6,
                  animation: 'pulse 3s ease-in-out infinite 1s',
                }} />
                <Globe 
                  size={48} 
                  strokeWidth={2.5} 
                  style={{ 
                    color: 'white',
                    position: 'relative',
                    zIndex: 1,
                    filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.2))',
                  }} 
                />
              </div>
              <h3 style={{
                fontSize: 'clamp(1.25rem, 2.2vw, 1.5rem)',
                fontWeight: 800,
                color: '#1a1a1a',
                marginBottom: '1rem',
                lineHeight: 1.2,
                letterSpacing: '-0.01em',
              }}>
                Multilingual Support
              </h3>
              <p style={{
                fontSize: 'clamp(0.9375rem, 1.6vw, 1.0625rem)',
                color: '#64748b',
                lineHeight: 1.7,
                margin: 0,
                fontWeight: 500,
              }}>
                Create stories in 13+ languages with bilingual mode—helping children become global citizens naturally
              </p>
            </div>

            {/* Card 4: Professional Narration */}
            <div className="feature-card-2026" style={{
              background: 'linear-gradient(to bottom, #ffffff 0%, #fafbfc 100%)',
              borderRadius: '28px',
              padding: 'clamp(2rem, 3.5vw, 3rem)',
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.02)',
              transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              border: '1px solid rgba(0, 0, 0, 0.04)',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
              e.currentTarget.style.boxShadow = '0 20px 60px rgba(245, 168, 208, 0.18), 0 8px 24px rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.borderColor = 'rgba(245, 168, 208, 0.3)';
              const glow = e.currentTarget.querySelector('.card-glow') as HTMLElement;
              if (glow) glow.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.02)';
              e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.04)';
              const glow = e.currentTarget.querySelector('.card-glow') as HTMLElement;
              if (glow) glow.style.opacity = '0';
            }}>
              <div className="card-glow" style={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(circle at center, rgba(245, 168, 208, 0.08) 0%, transparent 70%)',
                opacity: 0,
                transition: 'opacity 0.5s ease',
                pointerEvents: 'none',
              }} />
              
              <div style={{
                width: 'clamp(90px, 18vw, 110px)',
                height: 'clamp(90px, 18vw, 110px)',
                borderRadius: '28px',
                background: 'linear-gradient(135deg, #F5A8D0 0%, #E688B7 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.75rem',
                boxShadow: '0 16px 48px rgba(245, 168, 208, 0.45), inset 0 2px 12px rgba(255, 255, 255, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.2)',
                position: 'relative',
                transform: 'rotate(3deg)',
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(8px)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'rotate(0deg) scale(1.15) translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 24px 64px rgba(245, 168, 208, 0.6), inset 0 2px 12px rgba(255, 255, 255, 0.5), 0 0 0 2px rgba(255, 255, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'rotate(3deg) scale(1)';
                e.currentTarget.style.boxShadow = '0 16px 48px rgba(245, 168, 208, 0.45), inset 0 2px 12px rgba(255, 255, 255, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.2)';
              }}>
                {/* Animated Glow Ring */}
                <div style={{
                  position: 'absolute',
                  inset: '-4px',
                  borderRadius: '32px',
                  background: 'linear-gradient(135deg, rgba(245, 168, 208, 0.4), rgba(230, 136, 183, 0.4))',
                  filter: 'blur(12px)',
                  opacity: 0.6,
                  animation: 'pulse 3s ease-in-out infinite 1.5s',
                }} />
                <Mic 
                  size={48} 
                  strokeWidth={2.5} 
                  style={{ 
                    color: 'white',
                    position: 'relative',
                    zIndex: 1,
                    filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.2))',
                  }} 
                />
              </div>
              <h3 style={{
                fontSize: 'clamp(1.25rem, 2.2vw, 1.5rem)',
                fontWeight: 800,
                color: '#1a1a1a',
                marginBottom: '1rem',
                lineHeight: 1.2,
                letterSpacing: '-0.01em',
              }}>
                Professional Narration
              </h3>
              <p style={{
                fontSize: 'clamp(0.9375rem, 1.6vw, 1.0625rem)',
                color: '#64748b',
                lineHeight: 1.7,
                margin: 0,
                fontWeight: 500,
              }}>
                Studio-quality audio stories with lifelike voices and sound effects that bring every page to life
              </p>
            </div>
          </div>

        </div>

        {/* Featured Audio Books Section - 2026 AWARD-WINNING WOW EFFECT */}
        <div style={{ 
          position: 'relative',
          maxWidth: '1400px', 
          margin: '0 auto', 
          padding: '2.5rem 1.5rem 3rem',
          overflow: 'hidden',
        }}>
          {/* Magical Ambient Background Glow */}
          <div style={{
            position: 'absolute',
            top: '20%',
            left: '5%',
            width: '500px',
            height: '500px',
            background: 'radial-gradient(circle, rgba(201, 182, 228, 0.12) 0%, transparent 70%)',
            borderRadius: '50%',
            pointerEvents: 'none',
            filter: 'blur(60px)',
            animation: 'float 8s ease-in-out infinite',
          }} />
          <div style={{
            position: 'absolute',
            bottom: '20%',
            right: '5%',
            width: '450px',
            height: '450px',
            background: 'radial-gradient(circle, rgba(168, 216, 230, 0.12) 0%, transparent 70%)',
            borderRadius: '50%',
            pointerEvents: 'none',
            filter: 'blur(60px)',
            animation: 'float 8s ease-in-out infinite 2s',
          }} />

          {/* Section Header - Magical & Engaging */}
          <div style={{ textAlign: 'center', marginBottom: 'clamp(2.5rem, 5vw, 3.5rem)', position: 'relative', zIndex: 1 }}>
            {/* Floating Badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.75rem 1.75rem',
              background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.1) 0%, rgba(255, 184, 184, 0.1) 100%)',
              backdropFilter: 'blur(12px)',
              border: '2px solid rgba(255, 107, 107, 0.3)',
              borderRadius: '100px',
              marginBottom: '1.5rem',
              boxShadow: '0 8px 32px rgba(255, 107, 107, 0.2), inset 0 0 20px rgba(255, 255, 255, 0.1)',
              animation: 'pulse 3s ease-in-out infinite',
            }}>
              <span style={{ fontSize: '1.5rem', animation: 'rotate 4s linear infinite' }}>🎧</span>
              <span style={{
                fontSize: '1rem',
                fontWeight: 800,
                background: 'linear-gradient(135deg, #FF6B6B 0%, #FFB8B8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '0.03em',
                textTransform: 'uppercase',
              }}>
                Most Loved Stories
              </span>
            </div>

          <h2 style={{ 
              fontSize: 'clamp(2.25rem, 6vw, 3.5rem)', 
              fontWeight: 900, 
            marginBottom: '1rem',
              background: 'linear-gradient(135deg, #2D2D2D 0%, #1a1a1a 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
            }}>
              Magical Stories Kids Love
          </h2>
          <p style={{ 
              fontSize: 'clamp(1.125rem, 2.5vw, 1.375rem)', 
              color: '#64748b',
              fontWeight: 500,
              maxWidth: '700px',
              margin: '0 auto',
              lineHeight: 1.6,
            }}>
              Professionally narrated adventures that bring imagination to life
            </p>
          </div>

          {loadingFeaturedBooks ? (
            <div style={{ textAlign: 'center', padding: '4rem', color: '#666' }}>
              <div style={{ 
                display: 'inline-block',
                animation: 'float 3s ease-in-out infinite',
              }}>
                <Sparkles size={64} style={{ color: '#C9B6E4' }} />
              </div>
              <p style={{ marginTop: '1.5rem', fontSize: '1.25rem', fontWeight: 600 }}>Loading magical stories...</p>
            </div>
          ) : featuredBooks.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem', color: '#666' }}>
              <BookOpen size={64} style={{ opacity: 0.3, margin: '0 auto' }} />
              <p style={{ marginTop: '1.5rem', fontSize: '1.25rem' }}>No featured books available yet. Check back soon!</p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: 'clamp(1.5rem, 3vw, 2.5rem)',
              position: 'relative',
              zIndex: 1,
            }}>
              {featuredBooks.map((book, index) => {
                // Format duration properly
                const formatDuration = (seconds: number | null): string => {
                  if (!seconds || seconds <= 0) return '—';
                  if (seconds < 60) return `${Math.ceil(seconds)}s`;
                  const mins = Math.floor(seconds / 60);
                  const secs = Math.floor(seconds % 60);
                  if (mins < 10 && secs > 0) return `${mins}:${secs.toString().padStart(2, '0')}`;
                  return `${mins} min`;
                };

                return (
                <Link 
                  key={book.id}
                  href={`/audio-book/${book.id}`}
                  style={{ textDecoration: 'none' }}
                >
                  <div 
                      className="featured-book-card-2026"
                    style={{
                        background: 'linear-gradient(to bottom, #ffffff 0%, #fafbfc 100%)',
                        borderRadius: '28px',
                        padding: 'clamp(1.5rem, 2.5vw, 2rem)',
                      textAlign: 'center',
                        boxShadow: '0 8px 40px rgba(0, 0, 0, 0.08), 0 2px 12px rgba(0, 0, 0, 0.04)',
                        transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'pointer',
                      position: 'relative',
                        border: '1px solid rgba(255, 255, 255, 0.8)',
                        overflow: 'hidden',
                        animation: `fadeInUp 0.6s ease-out ${index * 0.1}s backwards`,
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                        e.currentTarget.style.boxShadow = '0 20px 60px rgba(168, 216, 230, 0.25), 0 8px 24px rgba(201, 182, 228, 0.15)';
                        e.currentTarget.style.border = '1px solid rgba(168, 216, 230, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = '0 8px 40px rgba(0, 0, 0, 0.08), 0 2px 12px rgba(0, 0, 0, 0.04)';
                        e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.8)';
                    }}
                  >
                      {/* Shimmer Effect on Hover */}
                    <div style={{
                      position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                        pointerEvents: 'none',
                      }} className="shimmer-2026" />

                      {/* Premium FEATURED Badge - High Contrast & Animated */}
                      <div style={{
                        position: 'absolute',
                        top: '1.25rem',
                        right: '1.25rem',
                        background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
                        padding: '0.5rem 1rem',
                        borderRadius: '12px',
                      fontSize: '0.75rem',
                        fontWeight: 800,
                      color: 'white',
                      textTransform: 'uppercase',
                      display: 'flex',
                      alignItems: 'center',
                        gap: '0.375rem',
                        boxShadow: '0 4px 20px rgba(255, 107, 107, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                        letterSpacing: '0.05em',
                        animation: 'badge-pulse 2s ease-in-out infinite',
                        zIndex: 10,
                      }}>
                        <Star size={14} fill="white" strokeWidth={0} style={{ animation: 'rotate 3s linear infinite' }} />
                        FEATURED
                    </div>

                      {/* Cover Image with Enhanced Effects */}
                    <div style={{
                      width: '100%',
                        height: 'clamp(260px, 30vw, 300px)',
                      background: book.coverImageUrl
                        ? `url(${book.coverImageUrl}) center/cover`
                          : 'linear-gradient(135deg, #C9B6E4 0%, #A8D8E6 100%)',
                      borderRadius: '20px',
                      marginBottom: '1.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), inset 0 0 0 1px rgba(255, 255, 255, 0.2)',
                        transition: 'transform 0.4s ease',
                      }}>
                        {/* Gradient Overlay for Better Contrast */}
                        <div style={{
                          position: 'absolute',
                          inset: 0,
                          background: 'linear-gradient(to bottom, transparent 0%, transparent 50%, rgba(0, 0, 0, 0.05) 100%)',
                          pointerEvents: 'none',
                        }} />

                        {/* Floating Play Button Overlay */}
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                          background: 'rgba(0, 0, 0, 0.2)',
                          backdropFilter: 'blur(4px)',
                        opacity: 0,
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                        className="play-overlay-premium">
                        <div style={{
                            width: '72px',
                            height: '72px',
                            background: 'linear-gradient(135deg, #C9B6E4 0%, #A8D8E6 100%)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                            boxShadow: '0 8px 32px rgba(201, 182, 228, 0.6), inset 0 2px 8px rgba(255, 255, 255, 0.3)',
                            transform: 'scale(0.9)',
                            transition: 'transform 0.3s ease',
                        }}>
                            <Play size={32} color="white" fill="white" strokeWidth={0} style={{ marginLeft: '4px' }} />
                        </div>
                      </div>
                    </div>

                      {/* Book Info with Better Hierarchy */}
                    <h3 style={{ 
                        fontSize: 'clamp(1.25rem, 2.5vw, 1.5rem)', 
                        fontWeight: 800, 
                        marginBottom: '0.875rem', 
                        color: '#1a1a1a',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.3,
                        letterSpacing: '-0.01em',
                        minHeight: '2.6em',
                    }}>
                      {book.title}
                    </h3>

                      {/* Enhanced Book Stats with Icons */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                        gap: 'clamp(1rem, 2vw, 1.5rem)',
                      marginBottom: '1.5rem',
                        padding: '0.75rem 1rem',
                        background: 'linear-gradient(135deg, rgba(168, 216, 230, 0.08) 0%, rgba(201, 182, 228, 0.08) 100%)',
                        borderRadius: '12px',
                        border: '1px solid rgba(168, 216, 230, 0.15)',
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.375rem',
                          color: '#64748b',
                          fontWeight: 600,
                          fontSize: 'clamp(0.875rem, 1.5vw, 0.9375rem)',
                        }}>
                          <BookOpen size={18} strokeWidth={2.5} style={{ color: '#C9B6E4' }} />
                          <span>{book.pageCount} pages</span>
                      </div>
                        <div style={{
                          width: '1px',
                          height: '18px',
                          background: 'linear-gradient(to bottom, transparent, rgba(168, 216, 230, 0.3), transparent)',
                        }} />
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.375rem',
                          color: '#64748b',
                          fontWeight: 600,
                          fontSize: 'clamp(0.875rem, 1.5vw, 0.9375rem)',
                        }}>
                          <Volume2 size={18} strokeWidth={2.5} style={{ color: '#A8D8E6' }} />
                          <span>{formatDuration(book.totalDuration)}</span>
                        </div>
                    </div>

                      {/* Premium Listen Button */}
                      <button 
                        className="listen-btn-2026"
                        style={{
                        width: '100%',
                        padding: '1rem 2rem',
                        background: 'linear-gradient(135deg, #C9B6E4 0%, #A8D8E6 100%)',
                      color: 'white',
                      border: 'none',
                        borderRadius: '16px',
                        fontSize: '1rem',
                        fontWeight: 800,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.75rem',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: '0 4px 20px rgba(168, 216, 230, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                        position: 'relative',
                        overflow: 'hidden',
                        letterSpacing: '0.02em',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                        e.currentTarget.style.background = 'linear-gradient(135deg, #A8D8E6 0%, #C9B6E4 100%)';
                        e.currentTarget.style.boxShadow = '0 8px 32px rgba(168, 216, 230, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.background = 'linear-gradient(135deg, #C9B6E4 0%, #A8D8E6 100%)';
                        e.currentTarget.style.boxShadow = '0 4px 20px rgba(168, 216, 230, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
                    }}>
                        <Play size={20} fill="white" strokeWidth={0} />
                        <span>Listen Now</span>
                        <Sparkles size={18} strokeWidth={2.5} />
                    </button>
                  </div>
                </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Pricing Section - 2026 Award-Winning Design */}
        <div id="pricing-section" style={{ 
          position: 'relative',
          maxWidth: '1400px', 
          margin: '0 auto', 
          padding: 'clamp(2.5rem, 5vw, 4rem) 1.5rem', 
          scrollMarginTop: '80px',
          overflow: 'hidden',
        }}>
          {/* Ambient Background Elements */}
          <div style={{
            position: 'absolute',
            top: '15%',
            left: '-8%',
            width: '500px',
            height: '500px',
            background: 'radial-gradient(circle, rgba(168, 216, 230, 0.08) 0%, transparent 70%)',
            borderRadius: '50%',
            pointerEvents: 'none',
            filter: 'blur(50px)',
          }} />
          <div style={{
            position: 'absolute',
            bottom: '10%',
            right: '-8%',
            width: '600px',
            height: '600px',
            background: 'radial-gradient(circle, rgba(201, 182, 228, 0.08) 0%, transparent 70%)',
            borderRadius: '50%',
            pointerEvents: 'none',
            filter: 'blur(50px)',
          }} />

          {/* Section Header */}
          <div style={{ textAlign: 'center', marginBottom: 'clamp(2rem, 4vw, 3rem)', position: 'relative', zIndex: 1 }}>
            <h2 style={{
              fontSize: 'clamp(2.25rem, 6vw, 4rem)',
              fontWeight: 900,
              background: 'linear-gradient(135deg, #2D2D2D 0%, #1a1a1a 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              marginBottom: '1.25rem',
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
            }}>
              Start Creating Magical Stories
            </h2>
            <p style={{
              fontSize: 'clamp(1.125rem, 2.2vw, 1.5rem)',
              color: '#64748b',
              fontWeight: 500,
              maxWidth: '800px',
              margin: '0 auto',
              lineHeight: 1.6,
            }}>
              Buy credits as you go, or unlock unlimited creativity with a monthly subscription
            </p>
          </div>

          {/* Credit Packages - 3 in a Row */}
          <div 
            id="credit-packages-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 'clamp(1.25rem, 2vw, 1.75rem)',
              maxWidth: '1200px',
              margin: '0 auto',
              position: 'relative',
              zIndex: 1,
              marginBottom: 'clamp(2rem, 3vw, 3rem)',
            }}>
            {PRICING_PACKAGES.map((pkg, index) => (
              <div
                key={pkg.type}
                className="pricing-card-2026"
                style={{
                  background: 'linear-gradient(to bottom, #ffffff 0%, #fafbfc 100%)',
                  borderRadius: '24px',
                  padding: 'clamp(1.5rem, 2.5vw, 2rem)',
                  textAlign: 'center',
                  boxShadow: pkg.featured
                    ? '0 12px 48px rgba(168, 216, 230, 0.2), 0 4px 16px rgba(201, 182, 228, 0.12)'
                    : '0 8px 32px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.02)',
                  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                  border: pkg.featured ? '2px solid rgba(168, 216, 230, 0.4)' : '1px solid rgba(0, 0, 0, 0.04)',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transform: pkg.featured ? 'scale(1.04)' : 'scale(1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = pkg.featured ? 'scale(1.08) translateY(-16px)' : 'translateY(-16px) scale(1.04)';
                  e.currentTarget.style.boxShadow = pkg.featured
                    ? '0 32px 90px rgba(168, 216, 230, 0.45), 0 16px 48px rgba(201, 182, 228, 0.3), 0 0 60px rgba(168, 216, 230, 0.2)'
                    : '0 24px 70px rgba(168, 216, 230, 0.25), 0 12px 36px rgba(0, 0, 0, 0.12)';
                  e.currentTarget.style.borderColor = pkg.featured ? 'rgba(168, 216, 230, 0.7)' : 'rgba(168, 216, 230, 0.4)';
                  const glow = e.currentTarget.querySelector('.card-glow') as HTMLElement;
                  if (glow) glow.style.opacity = '1';
                  const icon = e.currentTarget.querySelector('.pricing-icon') as HTMLElement;
                  if (icon) {
                    icon.style.transform = 'scale(1.15) rotate(5deg)';
                    icon.style.boxShadow = index === 0
                      ? '0 16px 50px rgba(102, 126, 234, 0.6), 0 8px 20px rgba(102, 126, 234, 0.5)'
                      : index === 1
                      ? '0 16px 50px rgba(168, 216, 230, 0.7), 0 8px 20px rgba(201, 182, 228, 0.6)'
                      : '0 16px 50px rgba(248, 200, 160, 0.6), 0 8px 20px rgba(248, 200, 160, 0.5)';
                  }
                  const iconGlow = e.currentTarget.querySelector('.icon-glow') as HTMLElement;
                  if (iconGlow) iconGlow.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = pkg.featured ? 'scale(1.04)' : 'scale(1)';
                  e.currentTarget.style.boxShadow = pkg.featured
                    ? '0 12px 48px rgba(168, 216, 230, 0.2), 0 4px 16px rgba(201, 182, 228, 0.12)'
                    : '0 8px 32px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.02)';
                  e.currentTarget.style.borderColor = pkg.featured ? 'rgba(168, 216, 230, 0.4)' : 'rgba(0, 0, 0, 0.04)';
                  const glow = e.currentTarget.querySelector('.card-glow') as HTMLElement;
                  if (glow) glow.style.opacity = '0';
                  const icon = e.currentTarget.querySelector('.pricing-icon') as HTMLElement;
                  if (icon) {
                    icon.style.transform = 'scale(1) rotate(0deg)';
                    icon.style.boxShadow = index === 0
                      ? '0 12px 40px rgba(102, 126, 234, 0.4), 0 4px 12px rgba(102, 126, 234, 0.3), inset 0 2px 8px rgba(255, 255, 255, 0.4)'
                      : index === 1
                      ? '0 12px 40px rgba(168, 216, 230, 0.5), 0 4px 12px rgba(201, 182, 228, 0.4), inset 0 2px 8px rgba(255, 255, 255, 0.4)'
                      : '0 12px 40px rgba(248, 200, 160, 0.4), 0 4px 12px rgba(248, 200, 160, 0.3), inset 0 2px 8px rgba(255, 255, 255, 0.4)';
                  }
                  const iconGlow = e.currentTarget.querySelector('.icon-glow') as HTMLElement;
                  if (iconGlow) iconGlow.style.opacity = '0';
                }}
              >
                {/* Hover Glow Effect */}
                <div className="card-glow" style={{
                  position: 'absolute',
                  inset: 0,
                  background: pkg.featured
                    ? 'radial-gradient(circle at center, rgba(168, 216, 230, 0.12) 0%, transparent 70%)'
                    : 'radial-gradient(circle at center, rgba(168, 216, 230, 0.08) 0%, transparent 70%)',
                  opacity: 0,
                  transition: 'opacity 0.5s ease',
                  pointerEvents: 'none',
                }} />

                {/* Featured Badge */}
                {pkg.featured && (
                  <div style={{ 
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    padding: '0.375rem 0.875rem',
                    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                    borderRadius: '10px',
                    fontSize: '0.6875rem',
                    fontWeight: 800,
                    color: 'white',
                    letterSpacing: '0.05em',
                    boxShadow: '0 4px 16px rgba(255, 215, 0, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                  }}>
                    <span style={{ fontSize: '0.75rem' }}>⭐</span>
                    BEST VALUE
                  </div>
                )}
                
                {/* Icon Badge */}
                <div 
                  className="pricing-icon"
                  style={{
                    width: 'clamp(70px, 14vw, 90px)',
                    height: 'clamp(70px, 14vw, 90px)',
                    borderRadius: '24px',
                    background: index === 0
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : index === 1
                      ? 'linear-gradient(135deg, #A8D8E6 0%, #C9B6E4 100%)'
                      : 'linear-gradient(135deg, #F8C8A0 0%, #E8C6B4 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1.5rem',
                    boxShadow: index === 0
                      ? '0 12px 40px rgba(102, 126, 234, 0.4), 0 4px 12px rgba(102, 126, 234, 0.3), inset 0 2px 8px rgba(255, 255, 255, 0.4)'
                      : index === 1
                      ? '0 12px 40px rgba(168, 216, 230, 0.5), 0 4px 12px rgba(201, 182, 228, 0.4), inset 0 2px 8px rgba(255, 255, 255, 0.4)'
                      : '0 12px 40px rgba(248, 200, 160, 0.4), 0 4px 12px rgba(248, 200, 160, 0.3), inset 0 2px 8px rgba(255, 255, 255, 0.4)',
                    position: 'relative',
                    transform: 'scale(1)',
                    transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    overflow: 'hidden',
                  }}
                >
                  {/* Animated Background Glow */}
                  <div className="icon-glow" style={{
                    position: 'absolute',
                    inset: '-20%',
                    background: pkg.featured
                      ? 'radial-gradient(circle, rgba(255, 255, 255, 0.6) 0%, transparent 70%)'
                      : 'radial-gradient(circle, rgba(255, 255, 255, 0.4) 0%, transparent 70%)',
                    opacity: 0,
                    transition: 'opacity 0.6s ease',
                    animation: 'pulse 3s ease-in-out infinite',
                  }} />
                  
                  {/* SVG Icon */}
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2))',
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    {index === 0 && (
                      // Starter - Rocket/Launch icon
                      <>
                        <path d="M12 2L15 8L22 9L17 14L18 21L12 18L6 21L7 14L2 9L9 8L12 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="white" fillOpacity="0.9"/>
                      </>
                    )}
                    {index === 1 && (
                      // Family - Heart icon
                      <>
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="white" fillOpacity="0.9"/>
                      </>
                    )}
                    {index === 2 && (
                      // Pro - Crown icon
                      <>
                        <path d="M2 20h20v2H2z" fill="white" fillOpacity="0.9"/>
                        <path d="M12 2L15 9L22 10L17 15L18 22L12 19L6 22L7 15L2 10L9 9L12 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="white" fillOpacity="0.9"/>
                      </>
                    )}
                  </svg>
                </div>

                {/* Plan Name */}
                <h3 style={{
                  fontSize: 'clamp(1.125rem, 2vw, 1.375rem)',
                  fontWeight: 800,
                  color: '#1a1a1a',
                  marginBottom: '0.75rem',
                  lineHeight: 1.2,
                  letterSpacing: '-0.01em',
                }}>
                  {pkg.name}
                </h3>
                
                {/* Price */}
                <div style={{ marginBottom: '0.625rem' }}>
                  <div style={{
                    fontSize: 'clamp(2.25rem, 4.5vw, 2.75rem)',
                    fontWeight: 900,
                    background: 'linear-gradient(135deg, #A8D8E6 0%, #C9B6E4 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    lineHeight: 1,
                    marginBottom: '0.5rem',
                  }}>
                    {pkg.price}
                  </div>
                  <div style={{
                    fontSize: 'clamp(0.875rem, 1.5vw, 1rem)',
                    fontWeight: 700,
                    color: '#64748b',
                    marginBottom: '0.25rem',
                  }}>
                  {pkg.credits} Credits
                </div>
                  <div style={{
                    fontSize: 'clamp(0.8125rem, 1.3vw, 0.875rem)',
                    color: '#94a3b8',
                    fontWeight: 500,
                  }}>
                    {pkg.pricePerBook} per story
                  </div>
                </div>

                {/* Features */}
                <ul style={{ 
                  listStyle: 'none', 
                  padding: 0, 
                  marginBottom: '1.5rem',
                  textAlign: 'left',
                  marginTop: '1.25rem',
                }}>
                  {pkg.features.slice(0, 3).map((feature, i) => (
                    <li key={i} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.625rem',
                      marginBottom: '0.625rem',
                      fontSize: 'clamp(0.875rem, 1.5vw, 0.9375rem)',
                      color: '#64748b',
                      fontWeight: 500,
                      lineHeight: 1.5,
                    }}>
                      <Check size={16} style={{ color: '#10B981', flexShrink: 0 }} strokeWidth={2.5} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                {/* CTA Button */}
                <button
                  onClick={() => handlePurchase(pkg.type)}
                  disabled={loading === pkg.type}
                  className="pricing-cta-btn"
                  style={{
                    width: '100%',
                    padding: 'clamp(0.875rem, 2vw, 1.125rem) 2rem',
                    background: pkg.featured
                      ? 'linear-gradient(135deg, #A8D8E6 0%, #C9B6E4 50%, #F8C8A0 100%)'
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundSize: '200% 100%',
                    color: 'white',
                    border: 'none',
                    borderRadius: '16px',
                    fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                    fontWeight: 900,
                    cursor: loading === pkg.type ? 'not-allowed' : 'pointer',
                    transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    boxShadow: pkg.featured
                      ? '0 10px 35px rgba(168, 216, 230, 0.5), 0 4px 15px rgba(201, 182, 228, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                      : '0 8px 28px rgba(102, 126, 234, 0.4), 0 4px 12px rgba(118, 75, 162, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                    letterSpacing: '0.03em',
                    textTransform: 'uppercase',
                    position: 'relative',
                    overflow: 'hidden',
                    isolation: 'isolate',
                  }}
                  onMouseEnter={(e) => {
                    if (loading !== pkg.type) {
                      e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                      e.currentTarget.style.backgroundPosition = '100% 0';
                      e.currentTarget.style.boxShadow = pkg.featured
                        ? '0 16px 50px rgba(168, 216, 230, 0.6), 0 8px 25px rgba(201, 182, 228, 0.5), 0 0 40px rgba(168, 216, 230, 0.3)'
                        : '0 14px 45px rgba(102, 126, 234, 0.6), 0 8px 20px rgba(118, 75, 162, 0.5)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.backgroundPosition = '0% 0';
                    e.currentTarget.style.boxShadow = pkg.featured
                      ? '0 10px 35px rgba(168, 216, 230, 0.5), 0 4px 15px rgba(201, 182, 228, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                      : '0 8px 28px rgba(102, 126, 234, 0.4), 0 4px 12px rgba(118, 75, 162, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
                  }}
                >
                  {/* Button Shine Effect */}
                  <span style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                    animation: 'buttonShine 3s ease-in-out infinite',
                    pointerEvents: 'none',
                  }} />
                  
                  {loading === pkg.type ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.625rem', position: 'relative', zIndex: 1 }}>
                      <span className="spinner" style={{ 
                        display: 'inline-block',
                        width: '1.125rem',
                        height: '1.125rem',
                        border: '2.5px solid rgba(255, 255, 255, 0.3)',
                        borderTopColor: 'white',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                      }}></span>
                      Processing...
                  </span>
                  ) : (
                    <span style={{ position: 'relative', zIndex: 1 }}>Get Started →</span>
                  )}
                </button>
              </div>
            ))}
          </div>

          {/* Subscription Plans - 2 in a Row */}
            <div 
              id="subscriptions-grid"
              style={{
              display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 'clamp(1.25rem, 2vw, 1.75rem)',
                maxWidth: '850px',
                margin: '0 auto',
                position: 'relative',
                zIndex: 1,
              }}>
            {SUBSCRIPTIONS.map((sub, index) => (
                <div 
                  key={sub.id}
                className="subscription-card-2026"
                  style={{ 
                  background: 'linear-gradient(to bottom, #ffffff 0%, #fafbfc 100%)',
                  borderRadius: '24px',
                  padding: 'clamp(1.5rem, 2.5vw, 2rem)',
                  textAlign: 'center',
                  boxShadow: sub.featured
                    ? '0 12px 48px rgba(102, 126, 234, 0.2), 0 4px 16px rgba(118, 75, 162, 0.12)'
                    : '0 8px 32px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.02)',
                  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                  border: sub.featured ? '2px solid rgba(102, 126, 234, 0.4)' : '1px solid rgba(0, 0, 0, 0.04)',
                    position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transform: sub.featured ? 'scale(1.03)' : 'scale(1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = sub.featured ? 'scale(1.06) translateY(-16px)' : 'translateY(-16px) scale(1.04)';
                  e.currentTarget.style.boxShadow = sub.featured
                    ? '0 32px 90px rgba(102, 126, 234, 0.45), 0 16px 48px rgba(118, 75, 162, 0.3), 0 0 60px rgba(102, 126, 234, 0.2)'
                    : '0 24px 70px rgba(248, 200, 160, 0.35), 0 12px 36px rgba(232, 198, 180, 0.25)';
                  e.currentTarget.style.borderColor = sub.featured ? 'rgba(102, 126, 234, 0.7)' : 'rgba(248, 200, 160, 0.5)';
                  const glow = e.currentTarget.querySelector('.card-glow') as HTMLElement;
                  if (glow) glow.style.opacity = '1';
                  const icon = e.currentTarget.querySelector('.subscription-icon') as HTMLElement;
                  if (icon) {
                    icon.style.transform = 'scale(1.15) rotate(5deg)';
                    icon.style.boxShadow = sub.featured
                      ? '0 16px 50px rgba(102, 126, 234, 0.6), 0 8px 20px rgba(102, 126, 234, 0.5)'
                      : '0 16px 50px rgba(248, 200, 160, 0.6), 0 8px 20px rgba(248, 200, 160, 0.5)';
                  }
                  const iconGlow = e.currentTarget.querySelector('.icon-glow') as HTMLElement;
                  if (iconGlow) iconGlow.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = sub.featured ? 'scale(1.03)' : 'scale(1)';
                  e.currentTarget.style.boxShadow = sub.featured
                    ? '0 12px 48px rgba(102, 126, 234, 0.2), 0 4px 16px rgba(118, 75, 162, 0.12)'
                    : '0 8px 32px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.02)';
                  e.currentTarget.style.borderColor = sub.featured ? 'rgba(102, 126, 234, 0.4)' : 'rgba(0, 0, 0, 0.04)';
                  const glow = e.currentTarget.querySelector('.card-glow') as HTMLElement;
                  if (glow) glow.style.opacity = '0';
                  const icon = e.currentTarget.querySelector('.subscription-icon') as HTMLElement;
                  if (icon) {
                    icon.style.transform = 'scale(1) rotate(0deg)';
                    icon.style.boxShadow = sub.featured
                      ? '0 12px 40px rgba(102, 126, 234, 0.4), 0 4px 12px rgba(102, 126, 234, 0.3), inset 0 2px 8px rgba(255, 255, 255, 0.4)'
                      : '0 12px 40px rgba(248, 200, 160, 0.4), 0 4px 12px rgba(248, 200, 160, 0.3), inset 0 2px 8px rgba(255, 255, 255, 0.4)';
                  }
                  const iconGlow = e.currentTarget.querySelector('.icon-glow') as HTMLElement;
                  if (iconGlow) iconGlow.style.opacity = '0';
                }}
              >
                {/* Hover Glow Effect */}
                <div className="card-glow" style={{
                  position: 'absolute',
                  inset: 0,
                  background: sub.featured
                    ? 'radial-gradient(circle at center, rgba(102, 126, 234, 0.12) 0%, transparent 70%)'
                    : 'radial-gradient(circle at center, rgba(232, 198, 180, 0.08) 0%, transparent 70%)',
                  opacity: 0,
                  transition: 'opacity 0.5s ease',
                  pointerEvents: 'none',
                }} />

                {/* Featured Badge */}
                  {sub.featured && (
                    <div style={{
                      position: 'absolute',
                      top: '1rem',
                      right: '1rem',
                    padding: '0.375rem 0.875rem',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '10px',
                    fontSize: '0.6875rem',
                    fontWeight: 800,
                      color: 'white',
                    letterSpacing: '0.05em',
                    boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                  }}>
                    <span style={{ fontSize: '0.75rem' }}>👑</span>
                      POPULAR
                    </div>
                  )}
                  
                {/* Icon Badge */}
                <div 
                  className="subscription-icon"
                  style={{
                    width: 'clamp(70px, 14vw, 90px)',
                    height: 'clamp(70px, 14vw, 90px)',
                    borderRadius: '24px',
                    background: sub.featured
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : 'linear-gradient(135deg, #F8C8A0 0%, #E8C6B4 100%)',
                    display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    margin: '0 auto 1.5rem',
                    boxShadow: sub.featured
                      ? '0 12px 40px rgba(102, 126, 234, 0.4), 0 4px 12px rgba(102, 126, 234, 0.3), inset 0 2px 8px rgba(255, 255, 255, 0.4)'
                      : '0 12px 40px rgba(248, 200, 160, 0.4), 0 4px 12px rgba(248, 200, 160, 0.3), inset 0 2px 8px rgba(255, 255, 255, 0.4)',
                    position: 'relative',
                    transform: 'scale(1)',
                    transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    overflow: 'hidden',
                  }}
                >
                  {/* Animated Background Glow */}
                  <div className="icon-glow" style={{
                    position: 'absolute',
                    inset: '-20%',
                    background: sub.featured
                      ? 'radial-gradient(circle, rgba(255, 255, 255, 0.6) 0%, transparent 70%)'
                      : 'radial-gradient(circle, rgba(255, 255, 255, 0.4) 0%, transparent 70%)',
                    opacity: 0,
                    transition: 'opacity 0.6s ease',
                    animation: 'pulse 3s ease-in-out infinite',
                  }} />
                  
                  {sub.featured ? <Crown size={44} color="white" strokeWidth={2} /> : <Zap size={44} color="white" strokeWidth={2} fill="white" fillOpacity={0.2} />}
                    </div>

                {/* Plan Name */}
                <h3 style={{
                  fontSize: 'clamp(1.125rem, 2vw, 1.375rem)',
                  fontWeight: 800,
                  color: '#1a1a1a',
                  marginBottom: '0.625rem',
                  lineHeight: 1.2,
                  letterSpacing: '-0.01em',
                }}>
                      {sub.name}
                    </h3>

                {/* Subscription Badge */}
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  padding: '0.25rem 0.75rem',
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
                  border: '1.5px solid rgba(102, 126, 234, 0.2)',
                  borderRadius: '100px',
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  color: '#667eea',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '1rem',
                }}>
                  <span style={{ fontSize: '0.75rem' }}>🔄</span>
                  Monthly
                    </div>

                {/* Price */}
                <div style={{ marginBottom: '0.625rem' }}>
                  <div style={{
                    fontSize: 'clamp(2.25rem, 4.5vw, 2.75rem)',
                    fontWeight: 900,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    lineHeight: 1,
                    marginBottom: '0.5rem',
                  }}>
                    {sub.price.split('/')[0]}<span style={{ fontSize: 'clamp(0.9375rem, 1.8vw, 1.125rem)', fontWeight: 700 }}>/month</span>
                  </div>
                  <div style={{
                    fontSize: 'clamp(0.875rem, 1.5vw, 1rem)',
                    fontWeight: 600,
                    color: '#64748b',
                  }}>
                      {sub.description}
                  </div>
                </div>

                {/* Features */}
                    <ul style={{ 
                      listStyle: 'none', 
                      padding: 0, 
                  marginBottom: '1.5rem',
                  textAlign: 'left',
                  marginTop: '1.25rem',
                    }}>
                      {sub.features.map((feature, i) => (
                        <li key={i} style={{ 
                          display: 'flex', 
                      alignItems: 'flex-start',
                      gap: '0.625rem',
                      marginBottom: '0.625rem',
                      fontSize: 'clamp(0.875rem, 1.5vw, 0.9375rem)',
                      color: '#64748b',
                      fontWeight: 500,
                      lineHeight: 1.5,
                    }}>
                      <Check size={16} style={{ color: sub.featured ? '#667eea' : '#10B981', flexShrink: 0, marginTop: '0.125rem' }} strokeWidth={2.5} />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                {/* CTA Button */}
                    <button
                      onClick={() => handleSubscribe(sub.id as 'amaya_plus' | 'amaya_pro')}
                      disabled={loading === sub.id}
                  className="subscription-cta-btn"
                  style={{
                    width: '100%',
                    padding: 'clamp(0.875rem, 2vw, 1.125rem) 2rem',
                    background: sub.featured
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #8B45FF 100%)'
                      : 'linear-gradient(135deg, #F8C8A0 0%, #E8C6B4 50%, #FFB8A0 100%)',
                    backgroundSize: '200% 100%',
                    color: 'white',
                    border: 'none',
                    borderRadius: '16px',
                    fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                    fontWeight: 900,
                    cursor: loading === sub.id ? 'not-allowed' : 'pointer',
                    transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    boxShadow: sub.featured
                      ? '0 10px 35px rgba(102, 126, 234, 0.5), 0 4px 15px rgba(118, 75, 162, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                      : '0 10px 35px rgba(248, 200, 160, 0.5), 0 4px 15px rgba(232, 198, 180, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                    letterSpacing: '0.03em',
                    textTransform: 'uppercase',
                    position: 'relative',
                    overflow: 'hidden',
                    isolation: 'isolate',
                  }}
                  onMouseEnter={(e) => {
                    if (loading !== sub.id) {
                      e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                      e.currentTarget.style.backgroundPosition = '100% 0';
                      e.currentTarget.style.boxShadow = sub.featured
                        ? '0 16px 50px rgba(102, 126, 234, 0.6), 0 8px 25px rgba(118, 75, 162, 0.5), 0 0 40px rgba(102, 126, 234, 0.3)'
                        : '0 16px 50px rgba(248, 200, 160, 0.6), 0 8px 25px rgba(232, 198, 180, 0.5), 0 0 40px rgba(248, 200, 160, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.backgroundPosition = '0% 0';
                    e.currentTarget.style.boxShadow = sub.featured
                      ? '0 10px 35px rgba(102, 126, 234, 0.5), 0 4px 15px rgba(118, 75, 162, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                      : '0 10px 35px rgba(248, 200, 160, 0.5), 0 4px 15px rgba(232, 198, 180, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
                  }}
                >
                  {/* Button Shine Effect */}
                  <span style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                    animation: 'buttonShine 3s ease-in-out infinite',
                    pointerEvents: 'none',
                  }} />
                  
                  {loading === sub.id ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.625rem', position: 'relative', zIndex: 1 }}>
                      <span className="spinner" style={{ 
                        display: 'inline-block',
                        width: '1.125rem',
                        height: '1.125rem',
                        border: '2.5px solid rgba(255, 255, 255, 0.3)',
                        borderTopColor: 'white',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                      }}></span>
                      Processing...
                    </span>
                  ) : (
                    <span style={{ position: 'relative', zIndex: 1 }}>Subscribe Now →</span>
                  )}
                    </button>
                </div>
              ))}
            </div>

          {/* Begin Your Journey Section - 2026 AWARD-WINNING */}
          <div style={{
            maxWidth: '1200px',
            margin: 'clamp(2.5rem, 4vw, 3.5rem) auto 0',
            padding: 'clamp(3rem, 5vw, 4rem)',
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.03) 0%, rgba(168, 216, 230, 0.05) 50%, rgba(201, 182, 228, 0.03) 100%)',
            borderRadius: '32px',
            border: '2px solid rgba(168, 216, 230, 0.2)',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(168, 216, 230, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
          }}>
            {/* Enhanced Decorative Glows */}
            <div style={{
              position: 'absolute',
              top: '-40%',
              left: '20%',
              width: '500px',
              height: '500px',
              background: 'radial-gradient(circle, rgba(16, 185, 129, 0.12) 0%, transparent 70%)',
              borderRadius: '50%',
              pointerEvents: 'none',
              filter: 'blur(60px)',
              animation: 'float 8s ease-in-out infinite',
            }} />
            <div style={{
              position: 'absolute',
              bottom: '-40%',
              right: '20%',
              width: '450px',
              height: '450px',
              background: 'radial-gradient(circle, rgba(201, 182, 228, 0.12) 0%, transparent 70%)',
              borderRadius: '50%',
              pointerEvents: 'none',
              filter: 'blur(60px)',
              animation: 'float 8s ease-in-out infinite 2s',
            }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
              {/* Sparkle Badge */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem 1.75rem',
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(52, 211, 153, 0.15) 100%)',
                backdropFilter: 'blur(12px)',
                borderRadius: '100px',
                marginBottom: '2rem',
                border: '2px solid rgba(16, 185, 129, 0.3)',
                boxShadow: '0 8px 24px rgba(16, 185, 129, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
              }}>
                <Zap size={20} strokeWidth={3} style={{ color: '#10B981' }} />
                <span style={{
                  fontSize: '0.9375rem',
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}>
                  Simple & Flexible
                </span>
              </div>

              <h3 style={{
                fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                fontWeight: 900,
                background: 'linear-gradient(135deg, #1a1a1a 0%, #2D2D2D 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '1.5rem',
                letterSpacing: '-0.03em',
                lineHeight: 1.1,
              }}>
                Begin Your Child's Learning Adventure
              </h3>
              
              <p style={{
                fontSize: 'clamp(1.125rem, 2.5vw, 1.5rem)',
                color: '#64748b',
                marginBottom: '2.5rem',
                lineHeight: 1.7,
                fontWeight: 500,
                maxWidth: '900px',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}>
                Explore <strong style={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontWeight: 700,
                }}>free previews</strong> instantly—no credit card needed. 
                Choose your path: buy credits as you go or subscribe for unlimited creativity. 
                <strong style={{ color: '#1a1a1a', fontWeight: 700 }}>Complete flexibility</strong>, always.
              </p>

              {/* Enhanced Trust Icons with Cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 'clamp(1.25rem, 2.5vw, 2rem)',
                maxWidth: '900px',
                margin: '0 auto',
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1.5rem',
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: '20px',
                  border: '1.5px solid rgba(168, 216, 230, 0.3)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(168, 216, 230, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.04)';
                  e.currentTarget.style.borderColor = 'rgba(168, 216, 230, 0.3)';
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #A8D8E6 0%, #C9B6E4 100%)',
                    borderRadius: '50%',
                    marginBottom: '0.75rem',
                    boxShadow: '0 4px 16px rgba(168, 216, 230, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                  }}>
                    <Check size={24} strokeWidth={3} style={{ color: 'white' }} />
                  </div>
                  <span style={{
                    fontSize: '1.0625rem',
                    color: '#1a1a1a',
                    fontWeight: 700,
                  }}>Instant Free Access</span>
                  <span style={{
                    fontSize: '0.875rem',
                    color: '#64748b',
                    fontWeight: 500,
                    lineHeight: 1.5,
                  }}>Preview any story instantly</span>
                </div>
                
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1.5rem',
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: '20px',
                  border: '1.5px solid rgba(168, 216, 230, 0.3)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(168, 216, 230, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.04)';
                  e.currentTarget.style.borderColor = 'rgba(168, 216, 230, 0.3)';
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)',
                    borderRadius: '50%',
                    marginBottom: '0.75rem',
                    boxShadow: '0 4px 16px rgba(252, 211, 77, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                  }}>
                    <Star size={24} strokeWidth={2.5} fill="white" style={{ color: 'white' }} />
                  </div>
                  <span style={{
                    fontSize: '1.0625rem',
                    color: '#1a1a1a',
                    fontWeight: 700,
                  }}>Pay As You Go</span>
                  <span style={{
                    fontSize: '0.875rem',
                    color: '#64748b',
                    fontWeight: 500,
                    lineHeight: 1.5,
                  }}>Credits or subscription—you choose</span>
                </div>
                
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1.5rem',
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: '20px',
                  border: '1.5px solid rgba(168, 216, 230, 0.3)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(168, 216, 230, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.04)';
                  e.currentTarget.style.borderColor = 'rgba(168, 216, 230, 0.3)';
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
                    borderRadius: '50%',
                    marginBottom: '0.75rem',
                    boxShadow: '0 4px 16px rgba(16, 185, 129, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                  }}>
                    <Heart size={24} strokeWidth={2.5} fill="white" style={{ color: 'white' }} />
                  </div>
                  <span style={{
                    fontSize: '1.0625rem',
                    color: '#1a1a1a',
                    fontWeight: 700,
                  }}>Total Freedom</span>
                  <span style={{
                    fontSize: '0.875rem',
                    color: '#64748b',
                    fontWeight: 500,
                    lineHeight: 1.5,
                  }}>Cancel or pause anytime, no hassle</span>
                </div>
              </div>
            </div>
          </div>

          {/* ✨ AWARD-WINNING 2026 RECIPE SHOWCASE SECTION ✨ */}
          {!loadingRecipes && recentRecipes.length > 0 && (
            <div style={{
              maxWidth: '1200px',
              margin: 'clamp(3rem, 5vw, 4.5rem) auto 0',
              padding: 'clamp(3rem, 5vw, 4.5rem)',
              background: 'linear-gradient(135deg, rgba(248, 200, 160, 0.04) 0%, rgba(253, 185, 136, 0.06) 50%, rgba(255, 229, 204, 0.04) 100%)',
              borderRadius: '40px',
              border: '2px solid rgba(248, 200, 160, 0.25)',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 25px 70px rgba(248, 200, 160, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
            }}>
              {/* Animated Background Glows */}
              <div style={{
                position: 'absolute',
                top: '-50%',
                left: '15%',
                width: '600px',
                height: '600px',
                background: 'radial-gradient(circle, rgba(248, 200, 160, 0.15) 0%, transparent 70%)',
                borderRadius: '50%',
                pointerEvents: 'none',
                filter: 'blur(80px)',
                animation: 'float 10s ease-in-out infinite',
              }} />
              <div style={{
                position: 'absolute',
                bottom: '-50%',
                right: '15%',
                width: '550px',
                height: '550px',
                background: 'radial-gradient(circle, rgba(253, 185, 136, 0.15) 0%, transparent 70%)',
                borderRadius: '50%',
                pointerEvents: 'none',
                filter: 'blur(80px)',
                animation: 'float 12s ease-in-out infinite 3s',
              }} />

              {/* Floating Recipe Sparkles */}
              {[...Array(15)].map((_, i) => (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    width: `${Math.random() * 8 + 4}px`,
                    height: `${Math.random() * 8 + 4}px`,
                    background: ['#F8C8A0', '#FDB988', '#FFE5CC', '#D97706'][i % 4],
                    borderRadius: '50%',
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    animation: `float ${8 + Math.random() * 6}s ease-in-out infinite ${Math.random() * 4}s`,
                    opacity: 0.3,
                    boxShadow: '0 0 12px currentColor',
                    pointerEvents: 'none',
                  }}
                />
              ))}

              <div style={{ position: 'relative', zIndex: 1 }}>
                {/* Header Section */}
                <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
                  {/* Animated Chef Hat Icon */}
                  <div style={{ 
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '90px',
                    height: '90px',
                    background: 'linear-gradient(135deg, #F8C8A0 0%, #FDB988 100%)',
                    borderRadius: '28px',
                    marginBottom: '1.75rem',
                    boxShadow: '0 15px 40px rgba(248, 200, 160, 0.4), inset 0 2px 0 rgba(255, 255, 255, 0.5)',
                    animation: 'float 4s ease-in-out infinite',
                    border: '3px solid rgba(255, 255, 255, 0.4)',
                  }}>
                    <ChefHat size={42} strokeWidth={2.5} style={{ color: 'white', filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))' }} />
                  </div>

                  {/* Badge */}
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem 1.75rem',
                    background: 'linear-gradient(135deg, rgba(248, 200, 160, 0.2) 0%, rgba(253, 185, 136, 0.2) 100%)',
                    backdropFilter: 'blur(12px)',
                    borderRadius: '100px',
                    marginBottom: '2rem',
                    border: '2px solid rgba(248, 200, 160, 0.4)',
                    boxShadow: '0 8px 24px rgba(248, 200, 160, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                  }}>
                    <UtensilsCrossed size={20} strokeWidth={3} style={{ color: '#D97706' }} />
                    <span style={{
                      fontSize: '0.9375rem',
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #D97706 0%, #F59E0B 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                    }}>
                      Kid-Approved Recipes
                    </span>
                  </div>

                  <h3 style={{
                    fontSize: 'clamp(2.25rem, 5.5vw, 3.75rem)',
                    fontWeight: 900,
                    background: 'linear-gradient(135deg, #D97706 0%, #F59E0B 50%, #FDB988 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    marginBottom: '1.25rem',
                    letterSpacing: '-0.03em',
                    lineHeight: 1.1,
                  }}>
                    Delicious Adventures in the Kitchen
                  </h3>

                  <p style={{
                    fontSize: 'clamp(1.125rem, 2.5vw, 1.375rem)',
                    color: '#64748b',
                    marginBottom: '0.5rem',
                    lineHeight: 1.7,
                    fontWeight: 500,
                    maxWidth: '800px',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                  }}>
                    Explore <strong style={{ 
                      background: 'linear-gradient(135deg, #D97706 0%, #F59E0B 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      fontWeight: 700,
                    }}>fun & healthy recipes</strong> that kids love to make and eat! 
                    From quick snacks to family meals, cooking together creates magical memories.
                  </p>
                </div>

                {/* Recipe Cards Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: 'clamp(1.5rem, 3vw, 2.5rem)',
                  marginBottom: '3rem',
                }}>
                  {recentRecipes.map((recipe, index) => (
                    <Link 
                      key={recipe.id} 
                      href={`/en/blogs/eats/${recipe.slug}`}
                      style={{ textDecoration: 'none' }}
                    >
                      <div
                        style={{
                          background: 'rgba(255, 255, 255, 0.95)',
                          backdropFilter: 'blur(12px)',
                          borderRadius: '28px',
                          overflow: 'hidden',
                          border: '2.5px solid rgba(248, 200, 160, 0.3)',
                          boxShadow: '0 8px 32px rgba(248, 200, 160, 0.15)',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          cursor: 'pointer',
                          position: 'relative',
                          animation: `fadeInUp 0.6s ease-out ${index * 0.15}s both`,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                          e.currentTarget.style.boxShadow = '0 20px 50px rgba(248, 200, 160, 0.3)';
                          e.currentTarget.style.borderColor = 'rgba(217, 119, 6, 0.5)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0) scale(1)';
                          e.currentTarget.style.boxShadow = '0 8px 32px rgba(248, 200, 160, 0.15)';
                          e.currentTarget.style.borderColor = 'rgba(248, 200, 160, 0.3)';
                        }}
                      >
                        {/* Recipe Image */}
                        {recipe.coverImage && (
                          <div style={{
                            width: '100%',
                            height: '220px',
                            background: `linear-gradient(135deg, rgba(248, 200, 160, 0.1) 0%, rgba(253, 185, 136, 0.1) 100%), url(${recipe.coverImage})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            position: 'relative',
                          }}>
                            {/* Gradient Overlay */}
                            <div style={{
                              position: 'absolute',
                              inset: 0,
                              background: 'linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.05) 100%)',
                            }} />
                            
                            {/* Quick Badge */}
                            <div style={{
                              position: 'absolute',
                              top: '1rem',
                              right: '1rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              padding: '0.5rem 1rem',
                              background: 'rgba(255, 255, 255, 0.95)',
                              backdropFilter: 'blur(10px)',
                              borderRadius: '100px',
                              border: '2px solid rgba(248, 200, 160, 0.5)',
                              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                            }}>
                              <Clock size={16} style={{ color: '#D97706' }} strokeWidth={2.5} />
                              <span style={{
                                fontSize: '0.8125rem',
                                fontWeight: 700,
                                color: '#D97706',
                              }}>
                                {recipe.readingTime} min
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Recipe Content */}
                        <div style={{ padding: '1.75rem' }}>
                          <h4 style={{
                            fontSize: '1.375rem',
                            fontWeight: 800,
                            color: '#1a1a1a',
                            marginBottom: '0.75rem',
                            lineHeight: 1.3,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}>
                            {recipe.title}
                          </h4>

                          <p style={{
                            fontSize: '0.9375rem',
                            color: '#64748b',
                            lineHeight: 1.6,
                            marginBottom: '1.25rem',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}>
                            {recipe.summary || 'Discover this amazing recipe!'}
                          </p>

                          {/* Tags */}
                          {recipe.tags && recipe.tags.length > 0 && (
                            <div style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '0.5rem',
                              marginBottom: '1.25rem',
                            }}>
                              {recipe.tags.slice(0, 3).map((tag, i) => (
                                <span
                                  key={i}
                                  style={{
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    color: '#D97706',
                                    background: 'rgba(248, 200, 160, 0.2)',
                                    padding: '0.375rem 0.875rem',
                                    borderRadius: '100px',
                                    border: '1.5px solid rgba(248, 200, 160, 0.4)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                  }}
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* CTA Button */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            color: '#D97706',
                            fontSize: '0.9375rem',
                            fontWeight: 700,
                            transition: 'gap 0.3s ease',
                          }}
                          className="recipe-cta">
                            <span>View Recipe</span>
                            <ArrowRight size={18} strokeWidth={3} />
                          </div>
                        </div>

                        {/* Hover Shine Effect */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '50%',
                          height: '100%',
                          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                          transition: 'left 0.6s ease',
                          pointerEvents: 'none',
                        }} className="shine-effect" />
                      </div>
                    </Link>
                  ))}
                </div>

                {/* View All Recipes Button */}
                <div style={{ textAlign: 'center' }}>
                  <Link href="/en/blogs/eats" style={{ textDecoration: 'none' }}>
                    <button
                      style={{
                        fontSize: '1.25rem',
                        fontWeight: 800,
                        padding: '1.25rem 3.5rem',
                        background: 'linear-gradient(135deg, #D97706 0%, #F59E0B 50%, #FDB988 100%)',
                        backgroundSize: '200% 200%',
                        border: 'none',
                        borderRadius: '100px',
                        color: 'white',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.875rem',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: '0 12px 32px rgba(217, 119, 6, 0.35), inset 0 2px 0 rgba(255, 255, 255, 0.3)',
                        position: 'relative',
                        overflow: 'hidden',
                        textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px) scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 20px 50px rgba(217, 119, 6, 0.5), inset 0 2px 0 rgba(255, 255, 255, 0.4)';
                        e.currentTarget.style.backgroundPosition = '100% 50%';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = '0 12px 32px rgba(217, 119, 6, 0.35), inset 0 2px 0 rgba(255, 255, 255, 0.3)';
                        e.currentTarget.style.backgroundPosition = '0% 50%';
                      }}
                    >
                      {/* Button Shine Effect */}
                      <span style={{
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                        animation: 'buttonShine 3s ease-in-out infinite',
                        pointerEvents: 'none',
                      }} />
                      
                      <span style={{ position: 'relative', zIndex: 1 }}>View All Recipes</span>
                      <UtensilsCrossed size={24} strokeWidth={3} style={{ position: 'relative', zIndex: 1 }} />
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bottom spacing */}
        <div style={{ height: '3rem' }} />
      </div>
    </>
  );
}

export default function HomePage2026() {
  return (
    <Suspense fallback={<div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom, #F9F5F0 0%, #FAF7F2 100%)' }} />}>
      <HomePage2026Content />
    </Suspense>
  );
}
